import { applicationPaths } from '@core/http/applicationPaths';
import { useApi } from '@core/http/useApi';
import { siteReducer } from '@core/store/entity/sites/SitesReducer';
import { EntityReducers } from '@core/store/EntityReducer';
import { useGetByIds } from '@core/store/hooks/entityHooks';
import { selectedAccountSelector } from '@core/store/library-stores/selectors';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { useEntityThunk } from '@hwm/ui-lib';
import { t } from '@lingui/macro';
import { useSidebar } from '@shared/hooks';
import { FilterType, SearchType, SelectedSitesListModel, SortColumnType, SortOrderType } from '@shared/models';
import { useCallback } from 'react';
import { batch } from 'react-redux';
import { SidebarKeys } from '../Sidebar.utils';
import { allSitesUiSlice, selectedSitesUiSlice, userSitesUiSlice } from './store/SitesUIReducer';
import { useSiteListState } from './useSiteListFiltersState';
import { useNotifications } from '@shared/hooks/useNotifications';
import { graphSitesUiSlice } from '@features/DataView/Graph/store/GraphSitesUIReducer';

export const useSiteZonesDataSource = () => {
  const { handleError } = useNotifications();

  const { setZoneFilter, setZoneOptions } = useSiteListState();

  const [getAccSiteZones, accSiteZonesLoading] = useApi<string[]>('', 'get');

  const handleGetAccSiteZones = useCallback(
    (accountId: string) => {
      const url = applicationPaths.accounts.zones({ accountId });
      accountId &&
        getAccSiteZones({ additionalOptions: { url } })
          .then((zones) => {
            setZoneFilter('');
            setZoneOptions(zones ?? []);
          })
          .catch((err) => {
            handleError(err, t`Site List`, t`There was a problem while trying to get Site Zone Options`);
          });
    },
    [getAccSiteZones, setZoneFilter, setZoneOptions, handleError]
  );

  return {
    getAccSiteZones: handleGetAccSiteZones,
    loading: accSiteZonesLoading,
  };
};

export const useSitesDataSource = () => {
  const dispatch = useAppDispatch();

  const { handleError, handleSuccess } = useNotifications();

  const { id } = useAppSelector(selectedAccountSelector) ?? {};

  const { totalAll, totalSelected, mainUserTotalSelected, loadingPending } = useAppSelector((state) => state.sites);

  const { currentlyLoadedPage } = useAppSelector((state) => state['all-sites-ui']) ?? {};

  const allSites = useGetByIds(
    siteReducer.entityAdapter,
    EntityReducers.sites,
    (state) => state['all-sites-ui'].loadedIds
  );

  const userSites = useGetByIds(
    siteReducer.entityAdapter,
    EntityReducers.sites,
    (state) => state['user-sites-ui'].loadedIds
  );

  const selectedSites = useGetByIds(
    siteReducer.entityAdapter,
    EntityReducers.sites,
    (state) => state['selected-sites-ui'].loadedIds
  );

  const [getUserSelectedSites] = useEntityThunk(
    siteReducer.thunks.getSelectedSiteList,
    'error msg',
    useCallback(
      (response) => {
        dispatch(
          userSitesUiSlice.actions.setIds({
            ids: response.items?.map((site) => site.id.toString()),
          })
        );
      },
      [dispatch]
    )
  );

  const handleGetUserSelectedSites = useCallback(
    (
      search?: string,
      diffId?: number,
      sortOrder?: SortOrderType,
      columnSort?: SortColumnType,
      searchType?: SearchType
    ) => {
      (id || diffId) &&
        getUserSelectedSites({
          filterType: FilterType.Selected,
          currentPage: 1,
          accountId: diffId ?? id,
          search,
          sortOrder,
          columnSort,
          searchType,
        });
    },
    [id, getUserSelectedSites]
  );

  const [getSelectedSites] = useEntityThunk(
    siteReducer.thunks.getSelectedSiteList,
    'error msg',
    useCallback(
      (response) => {
        dispatch(
          selectedSitesUiSlice.actions.setIds({
            ids: response.items?.map((site) => site.id.toString()),
          })
        );
      },
      [dispatch]
    )
  );

  const handleGetSelectedSites = useCallback(
    (
      search?: string,
      sortOrder?: SortOrderType,
      columnSort?: SortColumnType,
      searchType?: SearchType,
      zone?: string
    ) => {
      if (!id || currentlyLoadedPage === 1) return;

      getSelectedSites({
        currentPage: 1,
        accountId: id,
        search,
        sortOrder,
        columnSort,
        searchType,
        zone,
      });
    },
    [id, currentlyLoadedPage, getSelectedSites]
  );

  const [getAllSites] = useEntityThunk(
    siteReducer.thunks.getSiteList,
    'error msg',
    useCallback(
      (response) => {
        batch(() => {
          dispatch(allSitesUiSlice.actions.setCurrentlyLoadedPage(response.currentPage));
          dispatch(allSitesUiSlice.actions.upsertIds(response.items?.map((site) => site.id.toString())));
        });
      },
      [dispatch]
    )
  );

  const handleGetAllSites = useCallback(
    (
      currentPage: number,
      search?: string,
      sortOrder?: SortOrderType,
      columnSort?: SortColumnType,
      searchType?: SearchType,
      zone?: string
    ) => {
      if (!id || currentPage <= currentlyLoadedPage) return;

      getAllSites({
        currentPage,
        accountId: id,
        search,
        sortOrder,
        columnSort,
        searchType,
        zone,
      });
    },
    [id, currentlyLoadedPage, getAllSites]
  );

  const handleSetEmptySitesData = useCallback(() => {
    batch(() => {
      dispatch(siteReducer.slice.actions.setAllAndSelectedEmpty());
      dispatch(allSitesUiSlice.actions.setIds({ ids: [] }));
      dispatch(allSitesUiSlice.actions.setCurrentlyLoadedPage(0));
      dispatch(selectedSitesUiSlice.actions.setIds({ ids: [] }));
    });
  }, [dispatch]);

  const handleFavorites = useCallback(
    (
      add: boolean,
      siteId: number,
      hideSuccess?: boolean,
      setFilter?: (filterType: FilterType) => void,
      setSidebarState?: (newState: boolean) => void,
      cb?: () => void
    ) => {
      if (!add || mainUserTotalSelected < 10) {
        dispatch(siteReducer.thunks.handleFavorites({ add, siteId }))
          .then(() => {
            !hideSuccess &&
              handleSuccess(`${t`Site successfully`} ${add ? t`added to` : t`removed from`} selected`, t`Site List`);
            handleGetSelectedSites();
            handleGetUserSelectedSites();

            if (!add && mainUserTotalSelected === 1) {
              // set display to all sites
              setFilter?.(FilterType.All);
            }
            setSidebarState?.(false);

            cb?.();
          })
          .catch((err) => {
            handleError(err, t`Site List`, t`There was a problem while handling selected sites`);
          });
      } else {
        handleError(null, t`Site List`, t`Cannot add more than 10 sites to selected ones`);
      }
    },
    [mainUserTotalSelected, dispatch, handleError, handleSuccess, handleGetSelectedSites, handleGetUserSelectedSites]
  );

  const setGraphSites = useCallback(
    (ids: string[]) => {
      dispatch(graphSitesUiSlice.actions.setIds({ ids }));
    },
    [dispatch]
  );

  return {
    totalAll,
    userSites,
    totalSelected,
    selectedSites,
    mainUserTotalSelected,
    loading: loadingPending > 0,
    allSites: Object.values(allSites),
    handleFavorites,
    handleGetAllSites,
    handleGetSelectedSites,
    handleSetEmptySitesData,
    handleGetUserSelectedSites,
    setGraphSites,
  };
};

export const useSelectedSitesDataSource = () => {
  const dispatch = useAppDispatch();
  const { selectedSitesList } = useAppSelector((state) => state.sites);
  const { handleSuccess, handleError } = useNotifications();
  const { handleSidebarSection } = useSidebar();

  const context = t`Selected Sites List`;

  const [getSelectedLists, selectedLists, loadingGet, errorGet] = useApi<SelectedSitesListModel[]>(
    applicationPaths.sites.selectedList,
    'get'
  );

  const [createSelectedList, , loadingCreate, errorCreate] = useApi<SelectedSitesListModel, { name: string }>(
    applicationPaths.sites.selectedList,
    'post'
  );

  const [selectedListById, , loadingById, errorById] = useApi('', 'post');

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const handleGetSelectedLists = useCallback(() => {
    getSelectedLists().catch((err) => {
      handleError(err, context, t`There was a problem while loading selected sites lists`);
    });
  }, [getSelectedLists, handleError, context]);

  const handleLoadSelectedList = useCallback(
    (list?: SelectedSitesListModel, cb?: () => void, hideInfo?: boolean) => {
      selectedListById({
        additionalOptions: {
          url: applicationPaths.sites.selectedListLoad({ listId: list?.id || null }),
        },
      })
        .then(() => {
          dispatch(siteReducer.slice.actions.setUserSelectedEmpty());
          dispatch(siteReducer.slice.actions.setSelectedSitesList(list));

          !hideInfo && handleSuccess(list ? t`Selected Sites List loaded` : t`Selected Sites List cleared`, context);

          handleSidebarSection(SidebarKeys.siteList);
        })
        .catch((err) => {
          !hideInfo && handleError(err, context, t`There was a problem while loading selected sites list`);
        })
        .finally(() => {
          cb?.();
        });
    },
    [dispatch, selectedListById, handleSuccess, handleError, context, handleSidebarSection]
  );

  const handleCreateSelectedList = useCallback(
    (name: string) => {
      createSelectedList({ body: { name } })
        .then((list) => {
          handleLoadSelectedList(list);
          handleSuccess(t`Selected sites list created`, context);
        })
        .catch((err) => {
          handleError(err, context, t`There was a problem while creating selected sites list`);
        });
    },
    [createSelectedList, handleLoadSelectedList, handleSuccess, handleError, context]
  );

  const handleUpdateSelectedList = useCallback(
    (list: SelectedSitesListModel) => {
      selectedListById({
        additionalOptions: {
          url: applicationPaths.sites.selectedListId({ listId: list.id }),
          method: 'put',
        },
      })
        .then(() => {
          handleLoadSelectedList(list);
          handleSuccess(t`Selected sites list updated`, context);
        })
        .catch((err) => {
          handleError(err, context, t`There was a problem while updating selected sites list`);
        });
    },
    [selectedListById, handleLoadSelectedList, handleSuccess, handleError, context]
  );

  const handleRemoveSelectedList = useCallback(
    (list: SelectedSitesListModel) => {
      handleLoadSelectedList(null);

      selectedListById({
        additionalOptions: {
          url: applicationPaths.sites.selectedListId({ listId: list.id }),
          method: 'delete',
        },
      })
        .then(() => {
          handleLoadSelectedList(null);
          handleSuccess(t`Successfully removed selected list`, context);
        })
        .catch((err) => {
          handleError(err, context, t`There was a problem while removing selected list`);
        });
    },
    [context, handleError, handleSuccess, selectedListById, handleLoadSelectedList]
  );

  return {
    selectedSitesList,
    handleGetSelectedLists,
    handleCreateSelectedList,
    handleLoadSelectedList,
    handleUpdateSelectedList,
    handleRemoveSelectedList,
    selectedLists,
    loading: loadingGet || loadingCreate || loadingById,
    error: errorGet || errorCreate || errorById,
  };
};
